import { Pipe, PipeTransform } from '@angular/core';


import { PieceEventsSettingsGeneral } from 'extensions/st/piece-events/front/models/piece-events.models';


@Pipe({
  name: 'duration',
  standalone: true
})
export class DurationPipe implements PipeTransform {

  public transform(
    value?: string | null,
    settings?: PieceEventsSettingsGeneral | null,
    locale?: string
  ): string {
    if (!value) {
      return `0 ${this.getMinutesLabel(0, settings)}`;
    }

    const match = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/i.exec(value);
    if (!match) {
      return `0 ${this.getMinutesLabel(0, settings)}`;
    }

    const [, hours, mins, secs] = match;
    const totalSeconds = (Number(hours) || 0) * 3600
      + (Number(mins) || 0) * 60
      + (Number(secs) || 0);
    const totalMinutes = Math.round(totalSeconds / 60);
    const totalDays = Math.floor(totalMinutes / (24 * 60));
    const remainingMinutesAfterDays = totalMinutes % (24 * 60);
    const totalHours = Math.floor(remainingMinutesAfterDays / 60);
    const remainingMinutes = remainingMinutesAfterDays % 60;

    if (totalMinutes < 60) {
      const shortMinutesLabel = this.getMinutesLabel(totalMinutes, settings);
      return `${totalMinutes} ${shortMinutesLabel}`;
    }

    if (totalDays > 0) {
      const parts = [`${totalDays} ${this.getDaysLabel(totalDays, settings)}`];

      if (totalHours > 0) {
        parts.push(`${totalHours} ${this.getHoursLabel(totalHours, settings)}`);
      }

      if (remainingMinutes > 0) {
        parts.push(`${remainingMinutes} ${this.getMinutesLabel(remainingMinutes, settings)}`);
      }

      return this.joinParts(parts, locale);
    }

    const hoursLabel = this.getHoursLabel(totalHours, settings);

    if (remainingMinutes === 0) {
      return `${totalHours} ${hoursLabel}`;
    }

    const minutesLabel = this.getMinutesLabel(remainingMinutes, settings);

    return this.joinParts([
      `${totalHours} ${hoursLabel}`,
      `${remainingMinutes} ${minutesLabel}`
    ], locale);
  }

  private getDaysLabel(totalDays: number, settings?: PieceEventsSettingsGeneral | null): string {
    return totalDays === 1 ? settings?.durationDayText || 'day' : settings?.durationDaysText || 'days';
  }

  private getHoursLabel(totalHours: number, settings?: PieceEventsSettingsGeneral | null): string {
    return totalHours === 1 ? settings?.durationHourText || 'hour' : settings?.durationHoursText || 'hours';
  }

  private getMinutesLabel(totalMinutes: number, settings?: PieceEventsSettingsGeneral | null): string {
    return totalMinutes === 1 ? settings?.durationMinuteText || 'minute' : settings?.durationMinutesText || 'minutes';
  }

  private joinParts(parts: string[], locale?: string): string {
    if (parts.length <= 1) {
      return parts[0] || '';
    }

    const normalizedLocale = locale || 'en';
    const ListFormatConstructor = (Intl as { ListFormat?: new (locales?: string | string[], options?: { style?: 'long' | 'short' | 'narrow'; type?: 'conjunction' | 'disjunction' | 'unit' }) => { format: (items: string[]) => string } }).ListFormat;

    if (ListFormatConstructor) {
      return new ListFormatConstructor(normalizedLocale, {
        style: 'long',
        type: 'conjunction'
      }).format(parts);
    }

    if (parts.length === 2) {
      return `${parts[0]} and ${parts[1]}`;
    }

    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  }

}
